'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import IconValue from './atoms/IconValue';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Helpers ------------------------------ */

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && x && 'value' in x) return x.value ?? fallback;
    return x;
}
const trim = (s) => String(s ?? '').trim();

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['ctas', 'ctas-only', 'cta', 'cta-only', 'buttons', 'button'].includes(
            v
        )
    ) {
        return 'ctas-only';
    }
    if (['features', 'features-only', 'feature', 'feature-only'].includes(v)) {
        return 'features-only';
    }
    return 'text-content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

function getAlignmentClasses(alignment) {
    switch (String(alignment || 'left').toLowerCase()) {
        case 'center':
            return {
                textAlign: 'text-center',
                itemAlign: 'items-center mx-auto',
                buttonAlign: 'justify-center',
                containerAlign: 'mx-auto',
            };
        case 'right':
            return {
                textAlign: 'text-right',
                itemAlign: 'items-end ml-auto',
                buttonAlign: 'justify-end',
                containerAlign: 'ml-auto',
            };
        case 'left':
        default:
            return {
                textAlign: 'text-left',
                itemAlign: 'items-start',
                buttonAlign: 'justify-start',
                containerAlign: '',
            };
    }
}

function getColumnCount(featureCount) {
    if (featureCount <= 1) return 1;
    if (featureCount === 2 || featureCount === 4) return 2;
    return 3;
}

function getGridColumnsClass(columnCount) {
    if (columnCount === 1) return 'grid-cols-1';
    if (columnCount === 2) return 'grid-cols-1 lg:grid-cols-2';
    return 'grid-cols-1 lg:grid-cols-3';
}

function getOrphanClass(index, featureCount, columnCount, alignment = 'left') {
    if (columnCount !== 3 || featureCount <= 3) return '';

    const normalizedAlignment = String(alignment || 'left').toLowerCase();
    const remainder = featureCount % columnCount;
    const isLast = index === featureCount - 1;
    const isSecondToLast = index === featureCount - 2;

    if (remainder === 1 && isLast) {
        if (normalizedAlignment === 'right') return 'lg:col-start-3';
        if (normalizedAlignment === 'center') return 'lg:col-start-2';
        return '';
    }

    if (remainder === 2 && normalizedAlignment === 'right') {
        if (isSecondToLast) return 'lg:col-start-2';
        if (isLast) return 'lg:col-start-3';
    }

    return '';
}

function getDivPadding(boxBorder, boxColor) {
    return boxBorder === 'border-none' && !boxColor ? 'p-0' : 'p-6 lg:p-10';
}

const USP_STICKY_INTRO_LAYOUT = {
    slim: { top: 96, shift: -40 },
    compact: { top: 108, shift: -28 },
    standard: { top: 132, shift: -4 },
    large: { top: 156, shift: 20 },
};

function normalizeStickyNavHeight(value) {
    const raw = String(value || '')
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, '-');
    if (
        raw === 'slim' ||
        raw === 'tight' ||
        raw === 'thin' ||
        raw === 'minimal'
    ) {
        return 'slim';
    }
    if (
        raw === 'large' ||
        raw === 'taller' ||
        raw === 'extra-tall' ||
        raw === 'x-tall'
    ) {
        return 'large';
    }
    if (raw === 'standard' || raw === 'tall') return 'standard';
    return 'compact';
}

function readStickyNavHeightMode() {
    if (typeof document === 'undefined') return 'compact';
    const nav = document.querySelector('[data-component="Nav"]');
    return normalizeStickyNavHeight(
        nav?.dataset?.navHeight ||
            document.documentElement?.dataset?.navHeightMode ||
            'compact'
    );
}

/* -------------------------------- Component -------------------------------- */

export default function USP(props) {
    // Content
    const Anchor = trim(valOf(props.Anchor, 'usp'));
    const Tagline = trim(valOf(props.Tagline, ''));
    const Header = trim(valOf(props.Header, ''));
    const Body = trim(valOf(props.Body, ''));

    // Display
    const ColorScheme = valOf(props.ColorScheme, 'light');
    const VerticalSpacing = valOf(props.VerticalSpacing, 'medium');
    const Alignment = valOf(props.Alignment, 'left');
    const Layout = String(valOf(props.Layout, 'Default')).toLowerCase();
    const isStickyIntroLayout = [
        'two-column',
        'two column',
        'twocolumn',
        'sticky-intro',
        'sticky intro',
        'stickyintro',
    ].includes(Layout);

    // Animation
    const Animation = normalizeAnimation(valOf(props.Animation, 'fade'));
    const Duration = Number(valOf(props.Duration, 0.6)) || 0.6;
    const EditorPreview = Boolean(props.EditorPreview);
    const requestedGroup = mapGroup(
        valOf(props.AnimationGroup, 'text-content')
    );

    // Features
    const Features = Array.isArray(props.Features) ? props.Features : [];
    const columnCount = getColumnCount(Features.length);
    const numColumns = getGridColumnsClass(columnCount);
    const normalizedAlignment = String(Alignment || 'left').toLowerCase();
    const hasCenteredTwoOrphans =
        normalizedAlignment === 'center' &&
        columnCount === 3 &&
        Features.length > 3 &&
        Features.length % 3 === 2;
    const twoOrphansStart = Features.length - 2;

    // CTAs
    const CTA = trim(valOf(props.CTA, ''));
    const CustomCTALink = trim(valOf(props.CustomCTALink, ''));
    const SecondaryButtonText = trim(valOf(props.SecondaryButtonText, ''));
    const SecondaryButtonLink = trim(valOf(props.SecondaryButtonLink, ''));
    const OutlineButtonText = trim(valOf(props.OutlineButtonText, ''));
    const OutlineButtonLink = trim(valOf(props.OutlineButtonLink, ''));
    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    const AnimationGroup =
        requestedGroup === 'ctas-only' && !hasAnyCTA
            ? 'text-content'
            : requestedGroup;

    const animateText = AnimationGroup === 'text-content';
    const animateFeatures =
        AnimationGroup === 'text-content' || AnimationGroup === 'features-only';
    const animateCTAs = AnimationGroup === 'ctas-only';

    const {
        backgroundColor,
        textColor,
        headerColor,
        boxColor,
        boxTextColor,
        iconColor,
        boxBorder,
        secondaryButton,
        thirdButton,
        taglineColor,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);

    const vSpace = getVerticalSpacing(VerticalSpacing);
    const { textAlign, itemAlign, buttonAlign, containerAlign } =
        getAlignmentClasses(Alignment);
    const divPadding = getDivPadding(boxBorder, boxColor);

    const [prefersReduced, setPrefersReduced] = useState(false);
    const [stickyNavHeightMode, setStickyNavHeightMode] = useState('compact');
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);
    useEffect(() => {
        if (!isStickyIntroLayout || typeof window === 'undefined') return;

        let frame = 0;
        const updateNavHeightMode = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(() => {
                const nextMode = readStickyNavHeightMode();
                setStickyNavHeightMode((prev) =>
                    prev === nextMode ? prev : nextMode
                );
            });
        };

        updateNavHeightMode();
        window.addEventListener('resize', updateNavHeightMode);

        const observer =
            typeof MutationObserver !== 'undefined'
                ? new MutationObserver(updateNavHeightMode)
                : null;
        if (observer) {
            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-nav-height-mode'],
            });
            if (document.body) {
                observer.observe(document.body, {
                    attributes: true,
                    attributeFilter: ['data-nav-height'],
                    childList: true,
                    subtree: true,
                });
            }
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener('resize', updateNavHeightMode);
            observer?.disconnect();
        };
    }, [isStickyIntroLayout]);

    const stickyIntroLayout =
        USP_STICKY_INTRO_LAYOUT[stickyNavHeightMode] ||
        USP_STICKY_INTRO_LAYOUT.compact;
    const stickyIntroStyle = isStickyIntroLayout
        ? {
              '--usp-sticky-intro-top': `${stickyIntroLayout.top}px`,
              '--usp-sticky-intro-shift': `${stickyIntroLayout.shift}px`,
          }
        : undefined;

    const preset = Animation;
    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.5 };
    const baseKey = `usp-${AnimationGroup}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};

        if (isStagger) {
            const childVariants = {
                hidden: { opacity: 0, y: 16 },
                show: {
                    opacity: 1,
                    y: 0,
                    transition: { duration: Math.max(0.4, Duration - 0.2) },
                },
            };
            return { variants: childVariants };
        }

        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';

        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, preset, isStagger, Duration, EditorPreview]);

    const renderFeatureCard = (
        feature,
        index,
        extraClass = '',
        { disableOrphan = false } = {}
    ) => {
        // Try multiple possible keys: Icon, icon, IconClass, iconClass
        const iconSource =
            feature?.Icon ??
            feature?.icon ??
            feature?.IconClass ??
            feature?.iconClass;

        const icon = trim(valOf(iconSource, ''));
        const orphanClass = disableOrphan
            ? ''
            : getOrphanClass(
                  index,
                  Features.length,
                  columnCount,
                  normalizedAlignment
              );

        return (
            <MotionMaybe
                key={
                    (feature?._key ?? feature?.id ?? `usp-${index}`) +
                    '-' +
                    baseKey
                }
                enable={enableAnim && animateFeatures}
                k={`${baseKey}-feature-${index}`}
                motionProps={childMotionProps}
                className={`z-40 ${textAlign} ${boxColor} ${boxBorder} rounded-[min(1.5rem,var(--shape-radius))] ${divPadding} ${orphanClass} ${extraClass}`}
            >
                <div>
                    <div className="text-base leading-4">
                        {icon && (
                            <div
                                className={`flex flex-col ${itemAlign} mb-3 h-16 w-16`}
                            >
                                <IconValue
                                    value={icon}
                                    className={`text-5xl ${iconColor}`}
                                    style={{
                                        fontSize: feature?.size || '3rem',
                                    }}
                                />
                            </div>
                        )}

                        <MarkDown
                            variant="h3"
                            className={`font-display text-xl leading-6 ${boxTextColor}`}
                        >
                            {feature?.Title}
                        </MarkDown>
                    </div>

                    <div
                        className={`mt-3 flex flex-auto flex-col text-sm font-text leading-6 md:text-base md:leading-7 ${boxTextColor}`}
                    >
                        <MarkDown className="flex-auto">
                            {feature?.Description}
                        </MarkDown>
                    </div>
                </div>
            </MotionMaybe>
        );
    };

    const { element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign,
            enableAnim: enableAnim && animateCTAs,
            motionProps: childMotionProps,
        }
    );

    return (
        <section
            id={Anchor}
            className={`relative px-6 ${backgroundColor} lg:px-8 ${vSpace}`}
            style={backgroundStyle}
            data-json="usp"
        >
            <div
                className={`relative z-40 mx-auto max-w-2xl ${textAlign} lg:max-w-7xl`}
            >
                {isStickyIntroLayout ? (
                    <div className="grid gap-10 lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)] lg:items-start lg:gap-16">
                        <div
                            className="lg:sticky lg:top-[var(--usp-sticky-intro-top)] lg:mt-[var(--usp-sticky-intro-shift)]"
                            style={stickyIntroStyle}
                        >
                            {/* Intro */}
                            <MotionMaybe
                                enable={enableAnim && isStagger && animateText}
                                k={`${baseKey}-intro-container`}
                                motionProps={containerMotionProps}
                            >
                                {Tagline && (
                                    <MotionMaybe
                                        enable={enableAnim && animateText}
                                        k={`${baseKey}-tagline`}
                                        motionProps={childMotionProps}
                                    >
                                        <MarkDown
                                            className={`${containerAlign} max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${taglineColor}`}
                                        >
                                            {Tagline}
                                        </MarkDown>
                                    </MotionMaybe>
                                )}

                                {(Header || Body) && (
                                    <div
                                        className={`flex w-full max-w-md flex-col md:max-w-2xl ${containerAlign}`}
                                    >
                                        {Header && (
                                            <MotionMaybe
                                                enable={
                                                    enableAnim && animateText
                                                }
                                                k={`${baseKey}-header`}
                                                motionProps={childMotionProps}
                                            >
                                                <MarkDown
                                                    variant="h2"
                                                    className={`z-40 max-w-2xl font-display text-2xl tracking-tight ${headerColor} ${textAlign} md:text-3xl lg:text-4xl`}
                                                >
                                                    {Header}
                                                </MarkDown>
                                            </MotionMaybe>
                                        )}

                                        {Body && (
                                            <MotionMaybe
                                                enable={
                                                    enableAnim && animateText
                                                }
                                                k={`${baseKey}-body`}
                                                motionProps={childMotionProps}
                                            >
                                                <div className="z-40">
                                                    <MarkDown
                                                        className={`mt-6 max-w-2xl text-lg font-text leading-8 ${textColor} ${textAlign}`}
                                                    >
                                                        {Body}
                                                    </MarkDown>
                                                </div>
                                            </MotionMaybe>
                                        )}
                                    </div>
                                )}
                            </MotionMaybe>
                            {CTAs}
                        </div>

                        {Features.length > 0 && (
                            <MotionMaybe
                                enable={
                                    enableAnim && isStagger && animateFeatures
                                }
                                k={`${baseKey}-features-container`}
                                motionProps={containerMotionProps}
                                className="editable-component relative grid grid-cols-1 gap-8"
                            >
                                {Features.map((f, i) =>
                                    renderFeatureCard(f, i, '', {
                                        disableOrphan: true,
                                    })
                                )}
                            </MotionMaybe>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Intro */}
                        <MotionMaybe
                            enable={enableAnim && isStagger && animateText}
                            k={`${baseKey}-intro-container`}
                            motionProps={containerMotionProps}
                        >
                            {Tagline && (
                                <MotionMaybe
                                    enable={enableAnim && animateText}
                                    k={`${baseKey}-tagline`}
                                    motionProps={childMotionProps}
                                >
                                    <MarkDown
                                        className={`${containerAlign} max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${taglineColor}`}
                                    >
                                        {Tagline}
                                    </MarkDown>
                                </MotionMaybe>
                            )}

                            {(Header || Body) && (
                                <div
                                    className={`mb-16 flex w-full max-w-md flex-col md:max-w-2xl lg:mb-20 ${containerAlign}`}
                                >
                                    {Header && (
                                        <MotionMaybe
                                            enable={enableAnim && animateText}
                                            k={`${baseKey}-header`}
                                            motionProps={childMotionProps}
                                        >
                                            <MarkDown
                                                variant="h2"
                                                className={`z-40 max-w-2xl font-display text-2xl tracking-tight ${headerColor} ${textAlign} md:text-3xl lg:text-4xl`}
                                            >
                                                {Header}
                                            </MarkDown>
                                        </MotionMaybe>
                                    )}

                                    {Body && (
                                        <MotionMaybe
                                            enable={enableAnim && animateText}
                                            k={`${baseKey}-body`}
                                            motionProps={childMotionProps}
                                        >
                                            <div className="z-40">
                                                <MarkDown
                                                    className={`mt-6 max-w-2xl text-lg font-text leading-8 ${textColor} ${textAlign}`}
                                                >
                                                    {Body}
                                                </MarkDown>
                                            </div>
                                        </MotionMaybe>
                                    )}
                                </div>
                            )}
                        </MotionMaybe>

                        {/* Features grid */}
                        {Features.length > 0 && (
                            <MotionMaybe
                                enable={
                                    enableAnim && isStagger && animateFeatures
                                }
                                k={`${baseKey}-features-container`}
                                motionProps={containerMotionProps}
                                className={`editable-component relative mx-auto grid grid-cols-1 gap-8 lg:max-w-7xl ${numColumns}`}
                            >
                                <div className="contents">
                                    {Features.map((f, i) => {
                                        if (
                                            hasCenteredTwoOrphans &&
                                            i === twoOrphansStart + 1
                                        ) {
                                            return null;
                                        }

                                        if (
                                            hasCenteredTwoOrphans &&
                                            i === twoOrphansStart
                                        ) {
                                            const left = Features[i];
                                            const right = Features[i + 1];

                                            return (
                                                <div
                                                    key={`usp-two-orphans-${baseKey}`}
                                                    className="flex flex-col gap-8 lg:col-span-3 lg:flex-row lg:justify-center"
                                                >
                                                    {renderFeatureCard(
                                                        left,
                                                        i,
                                                        'lg:w-[calc((100%-4rem)/3)]'
                                                    )}
                                                    {renderFeatureCard(
                                                        right,
                                                        i + 1,
                                                        'lg:w-[calc((100%-4rem)/3)]'
                                                    )}
                                                </div>
                                            );
                                        }

                                        return renderFeatureCard(f, i);
                                    })}
                                </div>
                            </MotionMaybe>
                        )}
                        {CTAs}
                    </>
                )}
            </div>
        </section>
    );
}
